import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})

export class CognitoServiceService {
  private refreshInFlight$: Observable<boolean> | null = null;

  constructor(private http: HttpClient) { }

  public getTokenDetailsFromCognito(callbackCode: string): Observable<any> {
    const isInternal = !document.referrer.replace(/\/$/, "").toLowerCase().includes('saml') || document.referrer.replace(/\/$/, "") === environment.redirectURL
    const details: any = {
      grant_type: 'authorization_code',
      code: callbackCode,
      redirect_uri: environment.redirectURL,
      client_id: isInternal ? environment.sso_internal_client_id : environment.sso_external_client_id,
      scope: 'openid+profile'
    };

    const formBody = Object.keys(details)
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(details[key as keyof typeof details])}`)
      .join('&');

    const cognitoTokenURL = isInternal ? environment.internalCognitoTokenURL : environment.externalCognitoTokenURL;

    sessionStorage.setItem('isInternal', isInternal.toString());

    return this.http.post<any>(cognitoTokenURL,
      formBody, {
      responseType: 'json',
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      })
    }
    );
  }

  public ensureValidIdToken(refreshThresholdSeconds: number = 30): Observable<boolean> {
    if (!this.isIdTokenNearExpiry(refreshThresholdSeconds)) {
      return of(true);
    }

    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    const refreshToken = sessionStorage.getItem('refresh_token');
    if (!refreshToken) {
      return of(false);
    }

    const isInternal = this.isInternalClientSession();
    const clientId = isInternal ? environment.sso_internal_client_id : environment.sso_external_client_id;
    const cognitoTokenURL = isInternal ? environment.internalCognitoTokenURL : environment.externalCognitoTokenURL;

    const requestBody = {
      grant_type: 'refresh_token',
      client_id: clientId,
      refresh_token: refreshToken,
    };

    const formBody = Object.keys(requestBody)
      .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(requestBody[key as keyof typeof requestBody])}`)
      .join('&');

    this.refreshInFlight$ = this.http.post<any>(cognitoTokenURL,
      formBody,
      {
        responseType: 'json',
        headers: new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded'
        })
      }
    ).pipe(
      tap((response) => {
        if (!response?.access_token) {
          return;
        }

        sessionStorage.setItem('token', response.access_token);

        if (response.id_token) {
          sessionStorage.setItem('id_token', response.id_token);
        }

        if (response.refresh_token) {
          sessionStorage.setItem('refresh_token', response.refresh_token);
        }
      }),
      map((response) => !!response?.access_token),
      catchError((error) => {
        console.error('Error refreshing Cognito token:', error);
        return of(false);
      }),
      finalize(() => {
        this.refreshInFlight$ = null;
      }),
      shareReplay(1)
    );

    return this.refreshInFlight$;
  }

  private isIdTokenNearExpiry(refreshThresholdSeconds: number): boolean {
    const idToken = sessionStorage.getItem('id_token');
    if (!idToken) {
      return !!sessionStorage.getItem('refresh_token');
    }

    const expiry = this.getTokenExpiry(idToken);

    if (!expiry) {
      return true;
    }

    const nowSeconds = Math.floor(Date.now() / 1000);
    return expiry - nowSeconds <= refreshThresholdSeconds;
  }

  private isInternalClientSession(): boolean {
    const accessPayload = this.parseTokenPayload(sessionStorage.getItem('token'));
    const accessClientId = accessPayload?.['client_id'];
    if (typeof accessClientId === 'string') {
      return accessClientId === environment.sso_internal_client_id;
    }

    const idPayload = this.parseTokenPayload(sessionStorage.getItem('id_token'));
    const audience = idPayload?.['aud'];
    if (typeof audience === 'string') {
      return audience === environment.sso_internal_client_id;
    }

    return sessionStorage.getItem('isInternal') === 'true';
  }

  private getTokenExpiry(token: string | null): number | null {
    const payload = this.parseTokenPayload(token);
    if (typeof payload?.['exp'] === 'number') {
      return payload['exp'];
    }

    return null;
  }

  private parseTokenPayload(token: string | null): Record<string, any> | null {
    if (!token) {
      return null;
    }

    const parts = token.split('.');
    if (parts.length < 2 || !parts[1]) {
      return null;
    }

    try {
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      return JSON.parse(atob(base64));
    } catch {
      return null;
    }
  }
}
